import { SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ILabResultResponse } from '@services/lab-results.service';

// ==================== INTERFACE PRINCIPAL ====================
export interface IArchivoParaclinico {
  url: string;
  urlSafe?: SafeResourceUrl;
  nombre?: string;
  tipo?: string;
  esImagen?: boolean;
}

export interface IParaclinico {
  id?: string;
  tipo?: 'interno' | 'laboratorio' | 'gabinete';
  nombre: string;
  fecha: string;
  interpretacion?: string;
  pdfUrl?: string;
  interpretacionSafe?: SafeHtml;
  pdfUrlSafe?: SafeResourceUrl;
  created_at?: string;
  updated_at?: string;

  archivos?: IArchivoParaclinico[];

  // ==================== NUEVAS PROPIEDADES: LABORATORIO ====================
  analizadoAutomaticamente?: boolean;
  analisisMetadata?: {
    fechaAnalisis: string;
    totalParametros: number;
    parametrosFueraRango: number;
    confianzaPromedio: number;
  };
  // ========================================================================

  // ID numérico del backend (si viene de lab_results)
  backendId?: number;

  // ID del medical_record original (para identificar heredados)
  originalMedicalRecordId?: number;
}

// ==================== TIPOS DE CATEGORÍA ====================
export type TipoParaclinico = 'interno' | 'laboratorio' | 'gabinete';

export interface CategoriaParaclinico {
  tipo: TipoParaclinico;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  bgCard?: string; // Fondo de la card en lista
  borderCard?: string; // Color del borde de la card
}

// ==================== FILTROS ====================
export interface FiltrosParaclinicos {
  tipo?: TipoParaclinico | 'todos';
  fechaDesde?: string;
  fechaHasta?: string;
  busqueda?: string;
}

// ==================== ESTADO DE VISTA ====================
export type ModoVista = 'ver' | 'cargar' | 'editar';

export interface EstadoParaclinicosPage {
  paraclinicoSeleccionado: IParaclinico | null;
  modoVista: ModoVista;
  filtros: FiltrosParaclinicos;
  cargando: boolean;
  error: string | null;
}

// ==================== DATOS DE TABLA (para futuro) ====================
export interface DatosTablaParaclinico {
  fecha: string;
  valores: { [key: string]: number | string };
}

export interface TablaParaclinico {
  nombre: string;
  columnas: string[];
  filas: DatosTablaParaclinico[];
}

// ==================== INTERFACES PARA INTEGRACIÓN CON HISTÓRICO ====================
export interface IEventoHistorico {
  id?: number; // ID del evento en patient_timelines (para updates)
  fecha: string;
  nombre: string;
  descripcion: string;
  note_id?: number; // ID de la nota médica completa (opcional)
  paraclinico?: {
    id: string;
    backendId?: number; // ID numerico real en lab_results (para validacion backend)
    nombre: string;
    tipo: 'interno' | 'laboratorio' | 'gabinete';
    fecha: string;
    pdfUrl?: string;
  };
}

export interface IHistorico {
  titulo: string;
  eventos: IEventoHistorico[];
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Convierte una respuesta del backend (ILabResultResponse) al formato usado en el frontend (IParaclinico)
 *
 * @param labResult Respuesta del endpoint /api/lab-results
 * @returns Objeto IParaclinico compatible con el frontend
 */
export function labResultToParaclinico(labResult: ILabResultResponse): IParaclinico {
  // Construir array de archivos desde la nueva estructura
  let archivos: IArchivoParaclinico[] = [];

  if (labResult.files && labResult.files.length > 0) {
    // Nueva estructura: array de archivos con info completa
    archivos = labResult.files.map(file => ({
      url: file.url,
      nombre: file.nombre,
      tipo: file.tipo,
      esImagen: file.esImagen,
    }));
  } else if (labResult.file_urls && labResult.file_urls.length > 0) {
    // Estructura intermedia: solo URLs
    archivos = labResult.file_urls.map(url => ({
      url,
      esImagen: /\.(jpg|jpeg|png|gif|webp)$/i.test(url),
    }));
  } else if (labResult.patientFiles && labResult.patientFiles.length > 0) {
    // Estructura legacy con patientFiles
    archivos = labResult.patientFiles.map(pf => ({
      url: pf.file_path,
      nombre: pf.file_name,
      tipo: pf.mime_type,
      esImagen: pf.mime_type?.startsWith('image/') || false,
    }));
  }

  // URL principal (primera de la lista o legacy)
  const pdfUrl = archivos.length > 0
    ? archivos[0].url
    : (labResult.file_url || labResult.file?.url || (labResult.pdf_url ? `/storage/${labResult.pdf_url}` : undefined));

  // El backend devuelve campos en español (nombre, fecha, tipo, interpretacion)
  return {
    id: labResult.id.toString(),
    backendId: labResult.id,
    tipo: labResult.tipo || labResult.test_type,
    nombre: labResult.nombre || labResult.test_name || '',
    fecha: labResult.fecha || labResult.test_date || '',
    interpretacion: labResult.interpretacion || labResult.interpretation || '',
    pdfUrl,
    archivos: archivos.length > 0 ? archivos : undefined,
    analizadoAutomaticamente: labResult.analyzed_automatically,
    analisisMetadata: labResult.analysis_metadata,
    created_at: labResult.created_at,
    updated_at: labResult.updated_at,
    originalMedicalRecordId: labResult.medical_record_id,
  };
}

/**
 * Convierte un objeto IParaclinico del frontend al formato esperado por el backend
 *
 * @param paraclinico Objeto IParaclinico del frontend
 * @returns Objeto con el formato esperado por el endpoint /api/lab-results
 */
export function paraclinicoToLabResultData(paraclinico: Partial<IParaclinico>) {
  return {
    test_type: paraclinico.tipo,
    test_name: paraclinico.nombre || '',
    test_date: paraclinico.fecha || '',
    interpretation: paraclinico.interpretacion,
    analyzed_automatically: paraclinico.analizadoAutomaticamente,
    analysis_metadata: paraclinico.analisisMetadata,
  };
}
